import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { C } from '../lib/theme';

function ScoreRow({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 3, margin: '0.4rem 0 0.15rem' }}>
      {[0,1,2,3,4,5,6,7,8,9,10].map(n => (
        <button
          key={n}
          onClick={() => onChange(n)}
          style={{
            flex: 1, padding: '0.35rem 0', borderRadius: 5, border: 'none',
            background: value === n ? C.sage : C.mintLt,
            color: value === n ? '#fff' : C.gray,
            fontSize: '0.72rem', fontWeight: value === n ? 700 : 400,
            cursor: 'pointer', transition: 'background 0.1s',
          }}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

function Question({ label, sublabel, value, onChange, scaleLow, scaleHigh }) {
  return (
    <div style={{ marginBottom: '1.2rem' }}>
      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: C.carbon }}>{label}</div>
      {sublabel && <div style={{ fontSize: '0.78rem', color: C.gray, marginBottom: '0.2rem' }}>{sublabel}</div>}
      <ScoreRow value={value} onChange={onChange} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: C.gray }}>
        <span>{scaleLow}</span><span>{scaleHigh}</span>
      </div>
    </div>
  );
}

export default function BetaCheckin({ onSubmit, saving }) {
  const { t } = useTranslation();
  const [nps, setNps] = useState(null);
  const [treatmentUtility, setTreatmentUtility] = useState(null);
  const [featureScores, setFeatureScores] = useState({});
  const [suggestion, setSuggestion] = useState('');

  const FEATURES = [
    { key: 'chat',     label: t('checkin.feature_chat') },
    { key: 'weight',   label: t('checkin.feature_weight') },
    { key: 'planner',  label: t('checkin.feature_planner') },
    { key: 'exercise', label: t('checkin.feature_exercise') },
  ];

  const allAnswered = nps !== null && treatmentUtility !== null &&
    FEATURES.every(f => featureScores[f.key] !== undefined);

  function setFeature(key, val) {
    setFeatureScores(prev => ({ ...prev, [key]: val }));
  }

  function handleSubmit() {
    if (!allAnswered) return;
    onSubmit({ nps, treatmentUtility, featureScores, suggestion });
  }

  return (
    <div style={{ maxWidth: 480, margin: '0 auto' }}>
      <div style={{
        background: `linear-gradient(135deg, ${C.sage}, #40916C)`,
        borderRadius: 16, padding: '1.2rem', color: '#fff', marginBottom: '1rem',
      }}>
        <div style={{ fontSize: '1.5rem', marginBottom: '0.3rem' }}>🌿</div>
        <h2 style={{ margin: '0 0 0.3rem', fontFamily: 'Georgia, serif', fontSize: '1.2rem' }}>
          {t('checkin.title')}
        </h2>
        <p style={{ margin: 0, fontSize: '0.85rem', opacity: 0.85 }}>
          {t('checkin.subtitle')}
        </p>
      </div>

      <div style={{ background: '#fff', borderRadius: 16, padding: '1.4rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '1rem' }}>

        <Question
          label={t('checkin.q_nps')}
          value={nps}
          onChange={setNps}
          scaleLow={t('checkin.scale_low')}
          scaleHigh={t('checkin.scale_high')}
        />

        <Question
          label={t('checkin.q_utility')}
          value={treatmentUtility}
          onChange={setTreatmentUtility}
          scaleLow={t('checkin.scale_low')}
          scaleHigh={t('checkin.scale_high')}
        />

        <div style={{ marginBottom: '1.2rem' }}>
          <div style={{ fontSize: '0.88rem', fontWeight: 700, color: C.carbon, marginBottom: '0.8rem' }}>
            {t('checkin.features_title')}
          </div>
          {FEATURES.map(f => (
            <div key={f.key} style={{ marginBottom: '0.9rem' }}>
              <div style={{ fontSize: '0.82rem', color: C.carbon, marginBottom: '0.1rem' }}>{f.label}</div>
              <ScoreRow value={featureScores[f.key] ?? null} onChange={v => setFeature(f.key, v)} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65rem', color: C.gray }}>
                <span>{t('checkin.feature_scale_low')}</span><span>{t('checkin.feature_scale_high')}</span>
              </div>
            </div>
          ))}
        </div>

        <div>
          <div style={{ fontSize: '0.88rem', fontWeight: 700, color: C.carbon, marginBottom: '0.4rem' }}>
            {t('checkin.suggestion_label')} <span style={{ fontWeight: 400, color: C.gray }}>{t('checkin.suggestion_optional')}</span>
          </div>
          <textarea
            value={suggestion}
            onChange={e => setSuggestion(e.target.value)}
            placeholder={t('checkin.suggestion_placeholder')}
            rows={3}
            style={{
              width: '100%', padding: '0.7rem', borderRadius: 8,
              border: `1.5px solid ${C.mint}`, background: C.cream,
              fontSize: '0.88rem', resize: 'vertical', outline: 'none',
              boxSizing: 'border-box', color: C.carbon, lineHeight: 1.5,
            }}
          />
        </div>
      </div>

      <button
        onClick={handleSubmit}
        disabled={!allAnswered || saving}
        style={{
          width: '100%', padding: '1rem', borderRadius: 12, border: 'none',
          background: allAnswered ? C.sage : C.mint,
          color: '#fff', fontSize: '1rem', fontWeight: 700,
          cursor: allAnswered && !saving ? 'pointer' : 'default',
          transition: 'background 0.2s',
        }}
      >
        {saving ? t('checkin.sending') : t('checkin.submit_btn')}
      </button>

      {!allAnswered && (
        <p style={{ textAlign: 'center', fontSize: '0.78rem', color: C.gray, marginTop: '0.5rem' }}>
          {t('checkin.incomplete')}
        </p>
      )}
    </div>
  );
}
